import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';

/**
 * ActionMenu – reusable 3-dot contextual menu
 *
 * Props:
 *   items: Array<{
 *     label: string
 *     icon?: ReactNode
 *     onClick: () => void
 *     color?: 'default' | 'error' | 'warning' | 'success'
 *     disabled?: boolean
 *     hidden?: boolean        // exclude from menu entirely
 *   }>
 *   size?: 'small' | 'medium'
 *   tooltip?: string
 *   disabled?: boolean
 *   iconSx?: SxProps          // extra sx on the IconButton
 */

const COLOR_STYLES = {
  error: { color: '#b91c1c', hoverBg: 'rgba(185,28,28,0.06)' },
  warning: { color: '#b45309', hoverBg: 'rgba(180,83,9,0.06)' },
  success: { color: '#15803d', hoverBg: 'rgba(21,128,61,0.06)' },
  default: { color: 'inherit', hoverBg: 'rgba(27,63,135,0.05)' },
};

const ActionMenu = ({
  items = [],
  size = 'small',
  tooltip = 'Actions',
  disabled = false,
  iconSx = {},
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = (e) => {
    e?.stopPropagation?.();
    setAnchorEl(null);
  };

  const visibleItems = items.filter((item) => !item.hidden);
  if (visibleItems.length === 0) return null;

  return (
    <>
      <Tooltip title={tooltip} arrow placement="left">
        {/* span needed so tooltip works on disabled button */}
        <span>
          <IconButton
            size={size}
            onClick={handleOpen}
            disabled={disabled}
            aria-label="row actions"
            aria-haspopup="true"
            aria-expanded={open || undefined}
            sx={{
              borderRadius: '6px',
              color: 'text.secondary',
              transition: 'all 0.15s',
              '&:hover': {
                backgroundColor: 'rgba(27,63,135,0.07)',
                color: 'primary.main',
              },
              '&[aria-expanded="true"]': {
                backgroundColor: 'rgba(27,63,135,0.07)',
                color: 'primary.main',
              },
              ...iconSx,
            }}>
            <MoreVertIcon sx={{ fontSize: size === 'small' ? 18 : 22 }} />
          </IconButton>
        </span>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        elevation={0}
        slotProps={{
          paper: {
            sx: {
              minWidth: 180,
              borderRadius: '8px',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow:
                '0 4px 6px -1px rgba(15,23,42,0.08), 0 10px 24px -4px rgba(15,23,42,0.10)',
              py: 0.5,
              '& .MuiList-root': { py: 0.5 },
            },
          },
        }}>
        {visibleItems.map((item, idx) => {
          const style = COLOR_STYLES[item.color ?? 'default'];

          return (
            <MenuItem
              key={idx}
              disabled={item.disabled}
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
                item.onClick?.();
              }}
              sx={{
                mx: 0.5,
                borderRadius: '5px',
                py: 0.9,
                px: 1.5,
                fontSize: '0.8125rem',
                fontWeight: 450,
                color: item.disabled ? 'text.disabled' : style.color,
                gap: 0.75,
                transition: 'background-color 0.12s',
                '&:hover': { backgroundColor: style.hoverBg },
                '& .MuiListItemIcon-root': { color: 'inherit', minWidth: 0 },
              }}>
              {item.icon && (
                <ListItemIcon>
                  {React.cloneElement(item.icon, { sx: { fontSize: 16 } })}
                </ListItemIcon>
              )}
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: '0.8125rem',
                  fontWeight: 450,
                  lineHeight: 1.4,
                }}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default ActionMenu;